import {
  Payment,
  Subscription,
  CreatePaymentRequest,
  CreatePaymentResponse,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  CreateSubscriptionPlanRequest,
  SubscriptionCheckAccessResponse,
  SubscriptionEvent,
  SubscriptionPlan,
  SubscriptionUser,
  UpdateSubscriptionPlanRequest,
  UpdateSubscriptionUserRequest,
  AppleIapCredentials,
  UpdateAppleIapCredentialsRequest,
} from "@/types/breeze/AnythinkPay";
import { BaseService } from "../BaseService";
import { PaginatedResult } from "@/types/breeze/Any";

export class AnythinkPayService extends BaseService {
  // Get all payments for the Organisation
  public async getPayments(
    page: number = 1,
    pageSize: number = 25,
    filter: string = "",
  ): Promise<PaginatedResult<Payment>> {
    return this.getRequest(
      `/integrations/anythinkpay/payments?page=${page}&pageSize=${pageSize}${
        filter ? `&${filter}` : ""
      }`,
    );
  }

  // Get a specific payment by ID
  public async getPayment(id: string): Promise<Payment> {
    return this.getRequest(`/integrations/anythinkpay/payments/${id}`);
  }

  // Get payments by status
  public async getPaymentsByStatus(
    status: string,
    page: number = 1,
    pageSize: number = 25,
    filter: string = "",
  ): Promise<PaginatedResult<Payment>> {
    return this.getRequest(
      `/integrations/anythinkpay/payments/status/${status}?page=${page}&pageSize=${pageSize}${
        filter ? `&${filter}` : ""
      }`,
    );
  }

  // Create a new payment intent
  public async createPayment(
    request: CreatePaymentRequest,
  ): Promise<CreatePaymentResponse> {
    return this.postRequest(
      "/integrations/anythinkpay/payments",
      request,
      "Payment created successfully",
    );
  }

  // --- Subscription Methods ---

  // Get all subscriptions for the Organisation
  public async getSubscriptions(
    page: number = 1,
    pageSize: number = 25,
    filter: string = "",
  ): Promise<PaginatedResult<Subscription>> {
    return this.getRequest(
      `/integrations/anythinkpay/subscriptions?page=${page}&pageSize=${pageSize}${
        filter ? `&${filter}` : ""
      }`,
    );
  }

  // Get a specific subscription by ID
  public async getSubscription(id: string): Promise<Subscription> {
    return this.getRequest(`/integrations/anythinkpay/subscriptions/${id}`);
  }

  // Get subscriptions for a user (admin or self)
  public async getSubscriptionsByUser(userId: number): Promise<Subscription[]> {
    return this.getRequest(
      `/integrations/anythinkpay/subscriptions/by-user/${userId}`,
    );
  }

  public async getSubscriptionEvents(id: string): Promise<SubscriptionEvent[]> {
    return this.getRequest(
      `/integrations/anythinkpay/subscriptions/${id}/events`,
    );
  }

  // Check if current user has access to a subscription with the given name and optional status
  public async checkSubscriptionAccess(
    subscriptionName: string,
    status?: string,
  ): Promise<SubscriptionCheckAccessResponse> {
    const params = new URLSearchParams({
      subscription_name: subscriptionName,
    });
    if (status) params.set("status", status);
    return this.getRequest(
      `/integrations/anythinkpay/subscriptions/check-access?${params.toString()}`,
    );
  }

  // Get subscriptions by status
  public async getSubscriptionsByStatus(
    status: string,
    page: number = 1,
    pageSize: number = 25,
    filter: string = "",
  ): Promise<PaginatedResult<Subscription>> {
    return this.getRequest(
      `/integrations/anythinkpay/subscriptions/status/${status}?page=${page}&pageSize=${pageSize}${
        filter ? `&${filter}` : ""
      }`,
    );
  }

  // Create a new subscription
  public async createSubscription(
    request: CreateSubscriptionRequest,
  ): Promise<CreateSubscriptionResponse> {
    return this.postRequest(
      "/integrations/anythinkpay/subscriptions",
      request,
      "Subscription created successfully",
    );
  }

  // Cancel a subscription
  public async cancelSubscription(id: string): Promise<any> {
    return this.postRequest(
      `/integrations/anythinkpay/subscriptions/${id}/cancel`,
      {},
      "Subscription cancelled successfully",
    );
  }

  // Resume a subscription
  public async resumeSubscription(id: string): Promise<any> {
    return this.postRequest(
      `/integrations/anythinkpay/subscriptions/${id}/resume`,
      {},
      "Subscription resumed successfully",
    );
  }

  // --- Admin recovery tooling ---

  public async adminDeleteSubscription(id: string): Promise<void> {
    return this.deleteRequest(
      `/integrations/anythinkpay/subscriptions/${id}/admin`,
      "Subscription deleted",
    );
  }

  public async adminSetSubscriptionStatus(
    id: string,
    status: string,
    clearPeriod = false,
  ): Promise<any> {
    return this.postRequest(
      `/integrations/anythinkpay/subscriptions/${id}/admin/status`,
      { status, clear_period: clearPeriod },
      "Subscription status updated",
    );
  }

  public async adminRelinkSubscription(
    id: string,
    userId: number,
  ): Promise<any> {
    return this.postRequest(
      `/integrations/anythinkpay/subscriptions/${id}/admin/relink`,
      { user_id: userId },
      "Subscription relinked",
    );
  }

  public async adminResyncSubscription(id: string): Promise<any> {
    return this.postRequest(
      `/integrations/anythinkpay/subscriptions/${id}/admin/resync`,
      {},
      "Subscription re-synced",
    );
  }

  public async getSubscriptionUsers(
    subscriptionId: string,
  ): Promise<SubscriptionUser[]> {
    return this.getRequest(
      `/integrations/anythinkpay/subscriptions/${subscriptionId}/users`,
    );
  }

  public async updateSubscriptionUser(
    subscriptionId: string,
    request: UpdateSubscriptionUserRequest,
  ): Promise<void> {
    return this.putRequest(
      `/integrations/anythinkpay/subscriptions/${subscriptionId}/users`,
      request,
    );
  }

  public async deleteSubscriptionUser(
    subscriptionId: string,
    userId: number,
  ): Promise<void> {
    return this.deleteRequest(
      `/integrations/anythinkpay/subscriptions/${subscriptionId}/users/${userId}`,
    );
  }

  public async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    return this.getRequest("/integrations/anythinkpay/subscription-plans");
  }

  public async getSubscriptionPlan(id: number): Promise<SubscriptionPlan> {
    return this.getRequest(
      `/integrations/anythinkpay/subscription-plans/${id}`,
    );
  }

  public async createSubscriptionPlan(
    request: CreateSubscriptionPlanRequest,
  ): Promise<SubscriptionPlan> {
    return this.postRequest(
      "/integrations/anythinkpay/subscription-plans",
      request,
      "Plan created",
    );
  }

  public async updateSubscriptionPlan(
    id: number,
    request: UpdateSubscriptionPlanRequest,
  ): Promise<SubscriptionPlan> {
    return this.putRequest(
      `/integrations/anythinkpay/subscription-plans/${id}`,
      request,
      "Plan updated",
    );
  }

  public async deleteSubscriptionPlan(id: number): Promise<void> {
    return this.deleteRequest(
      `/integrations/anythinkpay/subscription-plans/${id}`,
      "Plan deleted",
    );
  }

  // --- Apple In-App Purchase credentials ---

  public async getAppleIapCredentials(): Promise<AppleIapCredentials> {
    return this.getRequest("/integrations/anythinkpay/apple-iap/credentials");
  }

  public async updateAppleIapCredentials(
    request: UpdateAppleIapCredentialsRequest,
  ): Promise<AppleIapCredentials> {
    return this.putRequest(
      "/integrations/anythinkpay/apple-iap/credentials",
      request,
      "Apple In-App Purchase settings saved",
    );
  }

  public async requestAppleTestNotification(
    environment: "sandbox" | "production",
  ): Promise<{ test_notification_token: string }> {
    return this.postRequest(
      `/integrations/anythinkpay/apple-iap/test-notification?environment=${environment}`,
      {},
      undefined,
      true,
      true,
    );
  }

  public async getAppleTestNotificationStatus(
    token: string,
    environment: "sandbox" | "production",
  ): Promise<{
    result: string | null;
    attempted_at: string | null;
    received_locally: boolean;
    received_at: string | null;
  }> {
    return this.postRequest(
      `/integrations/anythinkpay/apple-iap/test-notification/${token}/status?environment=${environment}`,
      {},
      undefined,
      true,
      true,
    );
  }

  public async getOffers(): Promise<import("@/types/breeze/AnythinkPay").Offer[]> {
    return this.getRequest("/integrations/anythinkpay/offers");
  }

  public async getOffer(
    id: string,
  ): Promise<import("@/types/breeze/AnythinkPay").Offer> {
    return this.getRequest(`/integrations/anythinkpay/offers/${id}`);
  }

  public async createOffer(request: {
    name: string;
    description?: string | null;
    kind: "discount" | "trial_extension" | "referral";
    redeemer_reward_json: string;
    referrer_reward_json?: string | null;
    eligibility_json?: string | null;
    valid_from?: string | null;
    valid_until?: string | null;
    total_redemption_cap?: number | null;
    per_user_redemption_cap?: number;
    status?: "active" | "paused" | "expired";
  }): Promise<import("@/types/breeze/AnythinkPay").Offer> {
    return this.postRequest(
      "/integrations/anythinkpay/offers",
      request,
      "Offer created",
    );
  }

  public async updateOffer(
    id: string,
    request: Partial<{
      name: string;
      description: string | null;
      redeemer_reward_json: string;
      referrer_reward_json: string | null;
      eligibility_json: string | null;
      valid_from: string | null;
      valid_until: string | null;
      total_redemption_cap: number | null;
      per_user_redemption_cap: number;
      status: "active" | "paused" | "expired";
    }>,
  ): Promise<import("@/types/breeze/AnythinkPay").Offer> {
    return this.putRequest(
      `/integrations/anythinkpay/offers/${id}`,
      request,
      "Offer updated",
    );
  }

  public async getOfferRedemptions(
    id: string,
    page: number = 1,
    pageSize: number = 50,
  ): Promise<import("@/types/breeze/AnythinkPay").OfferRedemption[]> {
    return this.getRequest(
      `/integrations/anythinkpay/offers/${id}/redemptions?page=${page}&pageSize=${pageSize}`,
    );
  }

  public async getUserBalances(
    userId: number,
  ): Promise<import("@/types/breeze/AnythinkPay").UserBalance[]> {
    return this.getRequest(`/integrations/anythinkpay/balances/users/${userId}`);
  }

  public async getUserBalanceHistory(
    userId: number,
    page: number = 1,
    pageSize: number = 50,
  ): Promise<import("@/types/breeze/AnythinkPay").UserBalanceLedgerEntry[]> {
    return this.getRequest(
      `/integrations/anythinkpay/balances/users/${userId}/history?page=${page}&pageSize=${pageSize}`,
    );
  }

  public async getTenantBalanceHolders(
    unitType?: string,
    page: number = 1,
    pageSize: number = 50,
  ): Promise<import("@/types/breeze/AnythinkPay").TenantBalanceHolder[]> {
    const unitParam = unitType ? `&unitType=${unitType}` : "";
    return this.getRequest(
      `/integrations/anythinkpay/balances/tenant/holders?page=${page}&pageSize=${pageSize}${unitParam}`,
    );
  }

  public async getTenantBalanceActivity(
    page: number = 1,
    pageSize: number = 50,
  ): Promise<import("@/types/breeze/AnythinkPay").UserBalanceLedgerEntry[]> {
    return this.getRequest(
      `/integrations/anythinkpay/balances/tenant/activity?page=${page}&pageSize=${pageSize}`,
    );
  }

  public async adjustUserBalance(
    userId: number,
    request: import("@/types/breeze/AnythinkPay").AdjustUserBalanceRequest,
  ): Promise<import("@/types/breeze/AnythinkPay").UserBalanceLedgerEntry> {
    return this.postRequest(
      `/integrations/anythinkpay/balances/users/${userId}/adjust`,
      request,
      "Balance adjusted",
    );
  }

  public async getTenantRedemptions(
    kinds?: string[],
    page: number = 1,
    pageSize: number = 50,
  ): Promise<import("@/types/breeze/AnythinkPay").OfferRedemption[]> {
    const kindsParam = kinds && kinds.length > 0 ? `&kinds=${kinds.join(",")}` : "";
    return this.getRequest(
      `/integrations/anythinkpay/offers/redemptions?page=${page}&pageSize=${pageSize}${kindsParam}`,
    );
  }

  public async getOfferCodes(
    id: string,
  ): Promise<import("@/types/breeze/AnythinkPay").OfferCode[]> {
    return this.getRequest(`/integrations/anythinkpay/offers/${id}/codes`);
  }

  public async getMyReferralCodes(): Promise<
    import("@/types/breeze/AnythinkPay").PersonalCode[]
  > {
    return this.getRequest("/integrations/anythinkpay/offers/me/codes");
  }

  public async getUserReferralCodesAll(
    userId: number,
  ): Promise<import("@/types/breeze/AnythinkPay").PersonalCode[]> {
    return this.getRequest(`/integrations/anythinkpay/offers/users/${userId}/codes`);
  }

  public async getUsersReferralCodes(
    userIds: number[],
  ): Promise<import("@/types/breeze/AnythinkPay").OwnedPersonalCode[]> {
    if (userIds.length === 0) return [];
    return this.postRequest(
      "/integrations/anythinkpay/offers/users/codes/batch",
      { owner_user_ids: userIds },
      undefined,
      true,
      true,
    );
  }

  public async getUsersRedeemedCodes(
    userIds: number[],
  ): Promise<import("@/types/breeze/AnythinkPay").UserRedeemedCode[]> {
    if (userIds.length === 0) return [];
    return this.postRequest(
      "/integrations/anythinkpay/offers/users/redeemed-codes/batch",
      { redeemer_user_ids: userIds },
      undefined,
      true,
      true,
    );
  }

  public async createOfferCode(
    id: string,
    request: { slug: string; owner_user_id?: number | null },
  ): Promise<import("@/types/breeze/AnythinkPay").OfferCode> {
    return this.postRequest(
      `/integrations/anythinkpay/offers/${id}/codes`,
      request,
      "Code created",
    );
  }
}

export const anythinkPayService = new AnythinkPayService();
